/**
 * Module-level bridge that lets non-React code (API client middleware, the
 * fetch wrapper) signal that the session has expired, so the React auth
 * context can transition to the login screen without a hard page reload.
 *
 * `notifyExpired()` also flips an "expired" flag that the login screen consumes
 * (via `consumeExpiredFlag()`) to explain why the user is back at sign-in.
 */
type SessionExpiryListener = () => void;

const listeners = new Set<SessionExpiryListener>();
let sessionExpired = false;

export const SessionExpiryNotifier = {
    /** Subscribe to session-expiry events. Returns an unsubscribe function. */
    subscribe(listener: SessionExpiryListener): () => void {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },

    /** Signal that the session has expired and notify all subscribers. */
    notifyExpired(): void {
        sessionExpired = true;
        listeners.forEach((listener) => {
            listener();
        });
    },

    /**
     * Read-and-clear the expiry flag. Returns true once after an expiry, then
     * false until the next `notifyExpired()`.
     */
    consumeExpiredFlag(): boolean {
        const wasExpired = sessionExpired;
        sessionExpired = false;
        return wasExpired;
    },
};
